import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { HologramSelect } from "./HologramSelect";

interface HologramNumberEditorProps {
  certificateId: number;
  currentHologramNumber: string;
  onUpdate: (newNumber: string) => void;
}

export function HologramNumberEditor({ 
  certificateId, 
  currentHologramNumber,
  onUpdate 
}: HologramNumberEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [newHologramNumber, setNewHologramNumber] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: availableHolograms, isLoading: isLoadingHolograms } = useQuery({
    queryKey: ["available-holograms"],
    queryFn: async () => {
      console.log('Fetching available holograms');
      const { data, error } = await supabase
        .from('holograms')
        .select('hologram_id, hologram_code, provider_id')
        .eq('status', 'available')
        .order('created_at');

      if (error) throw error;
      return data;
    },
  });

  // Set initial hologram number when data is loaded
  useEffect(() => {
    console.log('HologramNumberEditor useEffect - availableHolograms:', availableHolograms);
    console.log('Current newHologramNumber:', newHologramNumber);
    if (availableHolograms && availableHolograms.length > 0 && !newHologramNumber && isEditing) {
      console.log('Setting initial hologram number to:', availableHolograms[0].hologram_code);
      setNewHologramNumber(availableHolograms[0].hologram_code);
    }
  }, [availableHolograms, newHologramNumber, isEditing]);

  const checkHologramNumberUniqueness = async (hologramNumber: string) => {
    console.log('Checking if hologram number exists:', hologramNumber);
    
    const { data, error } = await supabase
      .from('certificates')
      .select('certificate_id')
      .eq('hologram_number', hologramNumber)
      .neq('certificate_id', certificateId)
      .maybeSingle();

    if (error) {
      console.error('Error checking hologram number:', error);
      throw error;
    }

    return data === null;
  };

  const handleUpdateHologramNumber = async () => {
    if (!newHologramNumber.trim()) {
      toast({
        title: "Error",
        description: "Please enter a hologram number",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const isUnique = await checkHologramNumberUniqueness(newHologramNumber);
      
      if (!isUnique) {
        toast({
          title: "Error",
          description: "This hologram number already exists. Please use a unique number.",
          variant: "destructive",
        });
        return;
      }

      // Find the hologram_id for the selected hologram_code
      const selectedHologram = availableHolograms?.find(
        h => h.hologram_code === newHologramNumber
      );

      if (!selectedHologram) {
        toast({
          title: "Error",
          description: "Selected hologram is no longer available",
          variant: "destructive",
        });
        return;
      }

      const { error: updateError } = await supabase
        .from('certificates')
        .update({ 
          hologram_number: newHologramNumber,
          hologram_id: selectedHologram.hologram_id 
        })
        .eq('certificate_id', certificateId);

      if (updateError) throw updateError;

      // Mark the hologram as used
      const { error: hologramError } = await supabase
        .from('holograms')
        .update({ 
          status: 'used',
          used_at: new Date().toISOString(),
        })
        .eq('hologram_id', selectedHologram.hologram_id);

      if (hologramError) throw hologramError;

      onUpdate(newHologramNumber);
      setIsEditing(false);
      
      toast({
        title: "Success",
        description: "Hologram number updated successfully",
      });

      // Invalidate queries to refresh the data
      await queryClient.invalidateQueries({ queryKey: ["certificates-list"] });
      await queryClient.invalidateQueries({ queryKey: ["available-holograms"] });
    } catch (error) {
      console.error('Error updating hologram number:', error);
      toast({
        title: "Error",
        description: "Failed to update hologram number",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartEditing = () => {
    setNewHologramNumber(""); // Reset the state before starting to edit
    setIsEditing(true);
  };

  if (isEditing) {
    return (
      <div className="border rounded-lg p-4 space-y-3">
        <h3 className="font-semibold text-lg">Hologram Number</h3>
        <div className="space-y-4">
          <HologramSelect
            value={newHologramNumber}
            onValueChange={setNewHologramNumber}
            availableHolograms={availableHolograms}
            isLoading={isLoadingHolograms}
            disabled={isSubmitting}
          />
          <div className="flex gap-2">
            <Button 
              onClick={handleUpdateHologramNumber}
              disabled={isSubmitting}
            >
              Save
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                setIsEditing(false);
                setNewHologramNumber(currentHologramNumber);
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-4 space-y-3">
      <h3 className="font-semibold text-lg">Hologram Number</h3>
      <div className="flex gap-2 items-center">
        <div className="text-lg font-medium">{currentHologramNumber}</div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={handleStartEditing}
        >
          Edit
        </Button>
      </div>
    </div>
  );
}